import { useEffect, useMemo, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import type { Database } from '@pizzeria/types/database.types';

import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

type RoleRow = Database['public']['Tables']['user_roles']['Row'];

export interface RoleAssignment {
  id: string;
  role: RoleRow['role'];
  restaurantId: string | null;
  restaurantSlug: string | null;
  restaurantName: string | null;
}

type AppMetadata = User['app_metadata'];

const buildAppMetadataRoleSignature = (metadata: AppMetadata | null | undefined) => {
  try {
    return JSON.stringify({
      role: metadata?.role ?? null,
      roles: metadata?.roles ?? null,
      is_admin: metadata?.is_admin ?? null,
    });
  } catch {
    return '';
  }
};

const deriveAppMetadataRoles = (metadata: AppMetadata | null | undefined): RoleAssignment[] => {
  if (!metadata) return [];
  const rawRoles: string[] = [];
  const appRole = metadata.role;
  const appRoles = metadata.roles;
  const isAdminFlag = metadata.is_admin;

  if (typeof appRole === 'string') {
    rawRoles.push(appRole);
  }
  if (Array.isArray(appRoles)) {
    rawRoles.push(...appRoles.map((value) => String(value)));
  } else if (typeof appRoles === 'string') {
    rawRoles.push(appRoles);
  }
  if (isAdminFlag === true) {
    rawRoles.push('admin');
  }

  const normalized = Array.from(new Set(rawRoles.map((role) => role.toLowerCase())));
  const valid = normalized.filter((role) => role === 'admin' || role === 'restaurant_staff' || role === 'driver');
  return valid.map((role, index) => ({
    id: `meta-${role}-${index}`,
    role: role as RoleRow['role'],
    restaurantId: null,
    restaurantSlug: null,
    restaurantName: null,
  }));
};

const mergeAssignments = (base: RoleAssignment[], extra: RoleAssignment[]) => {
  const keyFor = (role: RoleAssignment) =>
    `${role.role}:${role.restaurantId ?? ''}:${role.restaurantSlug ?? ''}`;
  const map = new Map<string, RoleAssignment>();
  for (const entry of base) {
    map.set(keyFor(entry), entry);
  }
  for (const entry of extra) {
    const key = keyFor(entry);
    if (!map.has(key)) {
      map.set(key, entry);
    }
  }
  return Array.from(map.values());
};

const ROLE_CACHE_KEY = 'lido:admin-roles:v1';
const ROLE_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

const readCachedRoles = (
  userId?: string | null,
  options: { allowStale?: boolean } = {}
): RoleAssignment[] => {
  if (!userId || typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(ROLE_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as {
      userId?: string;
      savedAt?: number;
      roles?: RoleAssignment[];
    };
    if (parsed.userId !== userId || !Array.isArray(parsed.roles)) {
      return [];
    }
    const savedAt = typeof parsed.savedAt === 'number' ? parsed.savedAt : 0;
    if (!options.allowStale && Date.now() - savedAt > ROLE_CACHE_TTL_MS) {
      return [];
    }
    return parsed.roles;
  } catch {
    return [];
  }
};

const writeCachedRoles = (userId: string, roles: RoleAssignment[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      ROLE_CACHE_KEY,
      JSON.stringify({ userId, savedAt: Date.now(), roles })
    );
  } catch {
    // ignore cache failures
  }
};

const clearCachedRoles = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(ROLE_CACHE_KEY);
  } catch {
    // ignore
  }
};

const hydrateFromCache = (assignments: RoleAssignment[], cachedRoles: RoleAssignment[]) => {
  const cachedByKey = new Map(
    cachedRoles.map((role) => [`${role.role}:${role.restaurantId ?? ''}`, role])
  );

  return assignments.map((assignment) => {
    const cached = cachedByKey.get(`${assignment.role}:${assignment.restaurantId ?? ''}`);
    if (!cached) return assignment;
    return {
      ...assignment,
      restaurantSlug: assignment.restaurantSlug ?? cached.restaurantSlug,
      restaurantName: assignment.restaurantName ?? cached.restaurantName,
    };
  });
};

export function useCurrentUserRoles() {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const appMetadataRoleSignature = buildAppMetadataRoleSignature(user?.app_metadata);
  const metadataRoles = useMemo(() => {
    if (!userId || !appMetadataRoleSignature) {
      return [];
    }
    try {
      return deriveAppMetadataRoles(JSON.parse(appMetadataRoleSignature) as AppMetadata);
    } catch {
      return [];
    }
  }, [appMetadataRoleSignature, userId]);
  const [roles, setRoles] = useState<RoleAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const resolvedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadRoles = async () => {
      if (!userId) {
        if (active) {
          resolvedUserIdRef.current = null;
          setRoles([]);
          setLoading(false);
          setError(null);
          clearCachedRoles();
        }
        return;
      }

      const cachedRoles = readCachedRoles(userId);
      if (cachedRoles.length > 0 || metadataRoles.length > 0) {
        setRoles(mergeAssignments(metadataRoles, cachedRoles));
      }

      // Only show the "checking" state the first time we resolve a given user, so a
      // background re-verification never blanks the panel.
      if (resolvedUserIdRef.current !== userId) {
        setLoading(true);
      }
      setError(null);

      const { data, error: rolesError } = await supabase
        .from('user_roles')
        .select('id, role, restaurant_id')
        .eq('user_id', userId);

      if (!active) return;

      if (rolesError) {
        setError(rolesError.message);
        const staleCachedRoles = readCachedRoles(userId, { allowStale: true });
        if (staleCachedRoles.length > 0 || metadataRoles.length > 0) {
          setRoles(mergeAssignments(metadataRoles, staleCachedRoles));
        } else {
          setRoles([]);
        }
      } else {
        const assignments: RoleAssignment[] = (data ?? []).map((row) => ({
          id: row.id,
          role: row.role,
          restaurantId: row.restaurant_id ?? null,
          restaurantSlug: null,
          restaurantName: null,
        }));

        if (assignments.length === 0) {
          resolvedUserIdRef.current = userId;
          setRoles([]);
          clearCachedRoles();
          setLoading(false);
          return;
        }

        const restaurantIds = Array.from(
          new Set(assignments.map((assignment) => assignment.restaurantId).filter(Boolean)) as Set<string>,
        );

        if (restaurantIds.length > 0) {
          const { data: restaurantRows, error: restaurantError } = await supabase
            .from('restaurants')
            .select('id, slug, name')
            .in('id', restaurantIds);

          if (restaurantError) {
            setError(restaurantError.message);
            const merged = hydrateFromCache(assignments, readCachedRoles(userId, { allowStale: true }));
            setRoles(merged);
            writeCachedRoles(userId, merged);
          } else {
            const restaurantMap = new Map<string, { slug: string | null; name: string | null }>();
            for (const row of restaurantRows ?? []) {
              restaurantMap.set(row.id, { slug: row.slug ?? null, name: row.name ?? null });
            }

            const hydrated = assignments.map((assignment) => {
              const restaurant = assignment.restaurantId ? restaurantMap.get(assignment.restaurantId) : null;
              return {
                ...assignment,
                restaurantSlug: restaurant?.slug ?? null,
                restaurantName: restaurant?.name ?? null,
              };
            });
            setRoles(hydrated);
            writeCachedRoles(userId, hydrated);
          }
        } else {
          setRoles(assignments);
          writeCachedRoles(userId, assignments);
        }
      }

      resolvedUserIdRef.current = userId;
      setLoading(false);
    };

    void loadRoles();

    return () => {
      active = false;
    };
  }, [metadataRoles, userId]);

  const isAdmin = useMemo(() => roles.some((role) => role.role === 'admin'), [roles]);
  const hasStaffAccess = useMemo(
    () => isAdmin || roles.some((role) => role.role === 'restaurant_staff'),
    [isAdmin, roles],
  );

  return {
    roles,
    loading: loading || authLoading,
    error,
    isAdmin,
    hasStaffAccess,
  };
}
